// CandidateGroupCard.jsx
import React, { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Checkbox,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

export default function CandidateGroupCard({
  group,
  personCoverage = {},            // ✅ key -> "full" | "partial" | "none"
  eligibleBuses = [],             // [{ bus, used, cap, free }]
  onAssignMissing,                // async (busId) => void
  onAssignSelected,               // async (busId, selectedPeople) => void
}) {
  const [selectedBusId, setSelectedBusId] = useState("");
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [expanded, setExpanded] = useState(false);

  const coverage = group?.coverage; // { total, full, partial, none, status }

  const people = useMemo(() => {
    const adults = Array.isArray(group?.adults) ? group.adults : [];
    const students = Array.isArray(group?.students) ? group.students : [];
    return [
      ...adults.map((p) => ({ ...p, personType: "ADULT" })),
      ...students.map((p) => ({ ...p, personType: "STUDENT" })),
    ].sort((a, b) => {
      const aIsAdult = String(a?.personType).toUpperCase() === "ADULT";
      const bIsAdult = String(b?.personType).toUpperCase() === "ADULT";
      if (aIsAdult !== bIsAdult) return aIsAdult ? -1 : 1;
      return String(a?.name || "").localeCompare(String(b?.name || ""));
    });
  }, [group?.adults, group?.students]);

  const counts = useMemo(() => {
    const adults = people.filter((p) => String(p?.personType).toUpperCase() === "ADULT").length;
    const total = people.length;
    return { adults, students: total - adults, total };
  }, [people]);

  const missingPeople = useMemo(() => {
    return people.filter((p) => {
      const type = String(p?.personType).toUpperCase();
      const id = type === "ADULT" ? (p.adultId ?? p.id) : (p.studentId ?? p.id);
      const key = `${type}:${id}`;
      const status = personCoverage?.[key] || "none";
      return status !== "full"; // ✅ partial + none are both still missing something
    });
  }, [people, personCoverage]);

  const missingCount = missingPeople.length;

  const kindLabel = useMemo(() => {
    const kind = String(group?.groupKind || "").trim();
    const subtype = group?.groupSubtype != null ? String(group.groupSubtype).trim() : "";
    if (kind && subtype) return `${kind} • ${subtype}`;
    return kind || subtype || null;
  }, [group?.groupKind, group?.groupSubtype]);

  const theme = useTheme();

  const sxForStatus =
    coverage?.status === "full"
      ? { borderColor: theme.palette.success.main, bgcolor: alpha(theme.palette.success.main, 0.08) }
      : coverage?.status === "partial"
      ? { borderColor: theme.palette.warning.main, bgcolor: alpha(theme.palette.warning.main, 0.08) }
      : { borderColor: theme.palette.error.main, bgcolor: alpha(theme.palette.error.main, 0.08) };

  function displayPersonName(p) {
    const parts = [p?.studentName, p?.studentSurname, p?.adultName, p?.adultSurname, p?.name]
      .filter(Boolean)
      .map((x) => String(x).trim())
      .filter(Boolean);

    if (parts.length) return parts.join(" ");

    const isAdult = String(p?.personType).toUpperCase() === "ADULT";
    const id = isAdult ? (p?.adultId ?? p?.id) : (p?.studentId ?? p?.id);

    return isAdult ? `Adult ${id ?? "—"}` : `Student ${id ?? "—"}`;
  }

  const toggleSelected = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllMissing = () => {
    setSelectedKeys(new Set(
      missingPeople.map((p) => {
        const type = String(p?.personType).toUpperCase();
        const id = type === "ADULT" ? (p.adultId ?? p.id) : (p.studentId ?? p.id);
        return `${type}:${id}`;
      })
    ));
  };

  const clearSelection = () => setSelectedKeys(new Set());

  const selectedMissingPeople = useMemo(() => {
    if (!selectedKeys.size) return [];
    return missingPeople.filter((p) => {
      const type = String(p?.personType).toUpperCase();
      const id = type === "ADULT" ? (p.adultId ?? p.id) : (p.studentId ?? p.id);
      return selectedKeys.has(`${type}:${id}`);
    });
  }, [missingPeople, selectedKeys]);

  // Once the list is expanded (or anyone is picked), the primary button acts on
  // the selection rather than assigning everyone missing — mirrors the
  // activities candidate card.
  const assignSelectedMode = expanded || selectedKeys.size > 0;

  const coverageLabel = useMemo(() => {
    const full = Number(coverage?.full || 0);
    const partial = Number(coverage?.partial || 0);
    const total = Number(coverage?.total || 0);

    if (partial > 0) return `${full + partial}/${total}`;
    return `${full}/${total}`;
  }, [coverage]);

  return (
    <Card variant="outlined" sx={{ height: "100%", ...sxForStatus }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
          <Typography fontWeight={800}>{group?.groupName || "Group"}</Typography>

          <Stack direction="row" spacing={0.5} alignItems="center">
            {kindLabel && <Chip size="small" label={kindLabel} variant="outlined" />}
            <Chip size="small" label={counts.total} />
            <Chip
              size="small"
              label={coverageLabel}
              variant={coverage?.status === "full" ? "filled" : "outlined"}
            />
          </Stack>
        </Stack>

        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          {counts.adults} adults • {counts.students} students
        </Typography>

        {/* Assign to + Assign all missing: always visible to anyone who may
            seat a bus (the handlers are withheld without the TRANSPORT grant). */}
        {missingCount > 0 && (onAssignMissing || onAssignSelected) && (
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel id={`assign-bus-${group?.groupKey}`}>Assign to…</InputLabel>
                <Select
                  labelId={`assign-bus-${group?.groupKey}`}
                  label="Assign to…"
                  value={selectedBusId}
                  onChange={(e) => setSelectedBusId(e.target.value)}
                >
                  {eligibleBuses.length === 0 ? (
                    <MenuItem value="" disabled>
                      No available buses
                    </MenuItem>
                  ) : (
                    eligibleBuses.map(({ bus, used, cap, free }) => (
                      <MenuItem key={bus.id} value={String(bus.id)}>
                        {bus.groupName} • {used}/{cap} • {free} free
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              {assignSelectedMode ? (
                <Button
                  variant="contained"
                  disabled={!selectedBusId || selectedMissingPeople.length === 0}
                  onClick={() => onAssignSelected?.(Number(selectedBusId), selectedMissingPeople)}
                >
                  Assign selected ({selectedMissingPeople.length})
                </Button>
              ) : (
                <Button
                  variant="contained"
                  disabled={!selectedBusId}
                  onClick={() => {
                    clearSelection();
                    onAssignMissing?.(Number(selectedBusId));
                  }}
                >
                  Assign all missing ({missingCount})
                </Button>
              )}
            </Stack>

            {/* Checkbox-related controls ONLY when expanded */}
            {expanded && (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Button size="small" onClick={selectAllMissing}>
                  Select all missing
                </Button>

                <Button size="small" onClick={clearSelection} disabled={selectedKeys.size === 0}>
                  Clear selection
                </Button>
              </Stack>
            )}
          </Stack>
        )}

        <Divider sx={{ my: 1 }} />

        <Accordion
          elevation={0}
          disableGutters
          expanded={expanded}
          onChange={(_, isExpanded) => {
            setExpanded(isExpanded);
            if (!isExpanded) clearSelection();
          }}
          sx={{ "&:before": { display: "none" } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ px: 0, minHeight: 32, "& .MuiAccordionSummary-content": { my: 0 } }}
          >
            <Typography variant="body2" fontWeight={700}>
              Travellers
            </Typography>
          </AccordionSummary>

          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            {people.length === 0 ? (
              <Typography variant="body2" sx={{ opacity: 0.75 }}>
                No travellers in this group.
              </Typography>
            ) : (
              <List dense disablePadding sx={{ mt: 0.25 }}>
                {people.map((p) => {
                  const type = String(p?.personType).toUpperCase();
                  const id = type === "ADULT" ? (p.adultId ?? p.id) : (p.studentId ?? p.id);
                  const key = `${type}:${id}`;
                  const isAdult = type === "ADULT";
                  const rowKey = isAdult ? `A-${id}` : `S-${id}`;

                  const coverageStatus = personCoverage?.[key] || "none";
                  const selectable = coverageStatus !== "full"; // ✅ partial + none selectable
                  const checked = selectable && selectedKeys.has(key);

                  return (
                    <ListItem key={rowKey} disableGutters sx={{ py: 0, px: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                        {expanded && selectable ? (
                          <Checkbox size="small" checked={checked} onChange={() => toggleSelected(key)} />
                        ) : (
                          <span style={{ width: 34 }} />
                        )}

                        {coverageStatus === "full" ? (
                          <CheckCircleIcon fontSize="small" color="success" />
                        ) : coverageStatus === "partial" ? (
                          <RemoveCircleOutlineIcon fontSize="small" color="warning" />
                        ) : (
                          <HighlightOffIcon fontSize="small" color="error" />
                        )}

                        <Typography
                          variant="body2"
                          fontWeight={isAdult ? 800 : 400}
                          sx={{ lineHeight: 1.2 }}
                        >
                          {displayPersonName(p)}
                        </Typography>
                      </Stack>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
}